import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Sparkles, BarChart3, Key } from 'lucide-react';
import {
    getCustomReport,
    getDomains,
} from '@/services/api';

import {
    CompactDateRangePicker,
    CompactDomainSelect,
    ReportFiltersModal,
    ReportView,
    InsightsModal,
    SummaryCards,
} from './index';
import {KeyValueDashboard} from "@/pages/Reports/components/key-values/KeyValueDashboard.tsx";

const CACHE_KEYS = {
    START_DATE: 'reports_start_date',
    END_DATE: 'reports_end_date',
    DOMAINS: 'reports_domains',
    METRICS: 'reports_metrics',
    DIMENSIONS: 'reports_dimensions',
    COMPARE: 'reports_compare',
    INSIGHTS: 'reports_insights',
    GROWTH: 'reports_growth',
    TOP_N: 'reports_top_n',
    COUNTRY: 'reports_country',
    AD_UNIT: 'reports_ad_unit',
    REPORT_TYPE: 'reports_type',
};

const getYesterday = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
};

const DEFAULT_METRICS = ['revenue', 'impressions', 'clicks', 'ecpm'];
const DEFAULT_DIMENSIONS = ['domain'];

type ReportType = 'ad_unit' | 'key_values' | null;

export default function Reports() {
    // Tipo de relatório selecionado
    const [reportType, setReportType] = useState<ReportType>(() => {
        const cached = localStorage.getItem(CACHE_KEYS.REPORT_TYPE);
        return cached as ReportType || null;
    });

    // Datas
    const [startDate, setStartDate] = useState(() => {
        const cached = localStorage.getItem(CACHE_KEYS.START_DATE);
        return cached || getYesterday();
    });

    const [endDate, setEndDate] = useState(() => {
        const cached = localStorage.getItem(CACHE_KEYS.END_DATE);
        return cached || getYesterday();
    });

    // Domínios
    const [selectedDomains, setSelectedDomains] = useState<string[]>(() => {
        const cached = localStorage.getItem(CACHE_KEYS.DOMAINS);
        return cached ? JSON.parse(cached) : [];
    });

    // Filtros adicionais
    const [selectedCountry, setSelectedCountry] = useState<string>(() => {
        return localStorage.getItem(CACHE_KEYS.COUNTRY) || '';
    });

    const [selectedAdUnit, setSelectedAdUnit] = useState<string>(() => {
        return localStorage.getItem(CACHE_KEYS.AD_UNIT) || '';
    });

    // Métricas e Dimensões
    const [selectedMetrics, setSelectedMetrics] = useState<string[]>(() => {
        const cached = localStorage.getItem(CACHE_KEYS.METRICS);
        return cached ? JSON.parse(cached) : DEFAULT_METRICS;
    });

    const [selectedDimensions, setSelectedDimensions] = useState<string[]>(() => {
        const cached = localStorage.getItem(CACHE_KEYS.DIMENSIONS);
        return cached ? JSON.parse(cached) : DEFAULT_DIMENSIONS;
    });

    // Opções de análise
    const [compareWithPrevious, setCompareWithPrevious] = useState(() => {
        const cached = localStorage.getItem(CACHE_KEYS.COMPARE);
        return cached ? JSON.parse(cached) : true;
    });

    const [includeInsights, setIncludeInsights] = useState(() => {
        const cached = localStorage.getItem(CACHE_KEYS.INSIGHTS);
        return cached ? JSON.parse(cached) : true;
    });

    const [includeGrowthAnalysis, setIncludeGrowthAnalysis] = useState(() => {
        const cached = localStorage.getItem(CACHE_KEYS.GROWTH);
        return cached ? JSON.parse(cached) : false;
    });

    const [topN, setTopN] = useState<number | null>(() => {
        const cached = localStorage.getItem(CACHE_KEYS.TOP_N);
        return cached ? JSON.parse(cached) : null;
    });

    const [minImpressions, setMinImpressions] = useState<number | null>(null);
    const [alertThreshold, setAlertThreshold] = useState<number>(15);

    // Modais
    const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);
    const [isInsightsModalOpen, setIsInsightsModalOpen] = useState(false);

    // Cache localStorage
    useEffect(() => {
        localStorage.setItem(CACHE_KEYS.REPORT_TYPE, reportType || '');
    }, [reportType]);
    useEffect(() => localStorage.setItem(CACHE_KEYS.START_DATE, startDate), [startDate]);
    useEffect(() => localStorage.setItem(CACHE_KEYS.END_DATE, endDate), [endDate]);
    useEffect(() => {
        localStorage.setItem(CACHE_KEYS.DOMAINS, JSON.stringify(selectedDomains));
    }, [selectedDomains]);
    useEffect(() => {
        localStorage.setItem(CACHE_KEYS.COUNTRY, selectedCountry);
    }, [selectedCountry]);
    useEffect(() => {
        localStorage.setItem(CACHE_KEYS.AD_UNIT, selectedAdUnit);
    }, [selectedAdUnit]);
    useEffect(() => {
        localStorage.setItem(CACHE_KEYS.METRICS, JSON.stringify(selectedMetrics));
    }, [selectedMetrics]);
    useEffect(() => {
        localStorage.setItem(CACHE_KEYS.DIMENSIONS, JSON.stringify(selectedDimensions));
    }, [selectedDimensions]);
    useEffect(() => {
        localStorage.setItem(CACHE_KEYS.COMPARE, JSON.stringify(compareWithPrevious));
    }, [compareWithPrevious]);
    useEffect(() => {
        localStorage.setItem(CACHE_KEYS.INSIGHTS, JSON.stringify(includeInsights));
    }, [includeInsights]);
    useEffect(() => {
        localStorage.setItem(CACHE_KEYS.GROWTH, JSON.stringify(includeGrowthAnalysis));
    }, [includeGrowthAnalysis]);
    useEffect(() => {
        localStorage.setItem(CACHE_KEYS.TOP_N, JSON.stringify(topN));
    }, [topN]);

    // Buscar domínios disponíveis
    const { data: domains = [] } = useQuery({
        queryKey: ['domains'],
        queryFn: () => getDomains(),
    });

    // Buscar dados do relatório customizado
    const {
        data: reportData,
        isLoading: isLoadingReport,
        isError: isErrorReport,
        error: errorReport,
        refetch: refetchReport,
    } = useQuery({
        queryKey: [
            'custom_report',
            reportType,
            selectedDomains,
            startDate,
            endDate,
            selectedMetrics,
            selectedDimensions,
            compareWithPrevious,
            includeInsights,
            includeGrowthAnalysis,
            topN,
            minImpressions,
            alertThreshold,
            selectedCountry,
            selectedAdUnit,
        ],
        queryFn: () =>
            getCustomReport({
                start: startDate,
                end: endDate,
                domains: selectedDomains.length > 0 ? selectedDomains : undefined,
                metrics: selectedMetrics,
                dimensions: selectedDimensions,
                compare_with_previous: compareWithPrevious,
                include_insights: includeInsights,
                include_growth_analysis: includeGrowthAnalysis,
                top_n: topN,
                min_impressions: minImpressions,
                alert_threshold: alertThreshold,
                country: selectedCountry || undefined,
                ad_unit_name: selectedAdUnit || undefined,
            }),
        enabled: reportType === 'ad_unit', // Só habilita para ad_unit
        retry: 2,
        retryDelay: 1000,
    });

    const handleDateChange = (start: string, end: string) => {
        setStartDate(start);
        setEndDate(end);
    };

    const handleReportTypeSelect = (type: ReportType) => {
        setReportType(type);
    };

    const handleBackToSelection = () => {
        setReportType(null);
    };

    // Contador de insights
    const insightsCount = reportData?.insights?.length || 0;
    const hasNewInsights = insightsCount > 0;

    // Se não selecionou tipo de relatório, mostra tela de seleção
    if (reportType === null) {
        return (
            <div className="space-y-6">
                {/* Header */}
                <div className="text-center">
                    <h1 className="text-3xl font-bold text-gray-900">Relatórios Avançados</h1>
                    <p className="text-sm text-gray-600 mt-2">
                        Selecione o tipo de relatório que deseja gerar
                    </p>
                </div>

                {/* Cards de Seleção */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto pt-8">
                    {/* Card Ad Unit */}
                    <button
                        onClick={() => handleReportTypeSelect('ad_unit')}
                        className="group relative bg-white rounded-xl shadow-lg border-2 border-gray-200 hover:border-blue-500 hover:shadow-xl transition-all duration-300 p-8 text-left"
                    >
                        <div className="flex flex-col items-center text-center space-y-4">
                            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                                <BarChart3 className="w-8 h-8 text-white" />
                            </div>

                            <div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">
                                    Relatórios Ad Unit
                                </h3>
                                <p className="text-sm text-gray-600">
                                    Análise de dados por blocos de anúncio e domínios
                                </p>
                            </div>

                            <div className="pt-4 border-t border-gray-200 w-full">
                                <ul className="text-xs text-gray-500 space-y-2">
                                    <li className="flex items-center gap-2">
                                        <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                                        Métricas por domínio
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                                        Análise de blocos de anúncio
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                                        Comparação de períodos
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-blue-600/5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </button>

                    {/* Card Key Values - ATIVO */}
                    <button
                        onClick={() => handleReportTypeSelect('key_values')}
                        className="group relative bg-white rounded-xl shadow-lg border-2 border-gray-200 hover:border-purple-500 hover:shadow-xl transition-all duration-300 p-8 text-left"
                    >
                        <div className="flex flex-col items-center text-center space-y-4">
                            <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                                <Key className="w-8 h-8 text-white" />
                            </div>

                            <div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">
                                    Relatórios Key Values
                                </h3>
                                <p className="text-sm text-gray-600">
                                    Análise avançada de chaves e valores com insights
                                </p>
                            </div>

                            <div className="pt-4 border-t border-gray-200 w-full">
                                <ul className="text-xs text-gray-500 space-y-2">
                                    <li className="flex items-center gap-2">
                                        <span className="w-1.5 h-1.5 bg-purple-500 rounded-full"></span>
                                        Performance por chave
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <span className="w-1.5 h-1.5 bg-purple-500 rounded-full"></span>
                                        Comparação de períodos
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <span className="w-1.5 h-1.5 bg-purple-500 rounded-full"></span>
                                        Mapas de calor por país
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-purple-600/5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </button>

                </div>
            </div>
        );
    }

    if (reportType === 'ad_unit') {
        return (
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={handleBackToSelection}
                            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Voltar"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>

                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">
                                Relatórios Ad Unit
                            </h1>
                            <p className="text-xs md:text-sm text-gray-600">
                                Análises de blocos de anúncio e domínios com insights automáticos
                            </p>
                        </div>
                    </div>
                </div>

                {/* Barra de Filtros Compacta */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 md:gap-3 bg-white rounded-lg p-2 md:p-3 shadow-sm border border-gray-200">
                    <CompactDomainSelect
                        value={selectedDomains}
                        onChange={setSelectedDomains}
                        domains={domains}
                    />

                    <div className="hidden sm:block h-6 w-px bg-gray-300" />

                    <CompactDateRangePicker
                        startDate={startDate}
                        endDate={endDate}
                        onDateChange={handleDateChange}
                    />

                    <div className="hidden lg:block h-6 w-px bg-gray-300" />

                    {/* Botão de Filtros Avançados */}
                    <button
                        onClick={() => setIsFiltersModalOpen(true)}
                        className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                        </svg>
                        Filtros
                    </button>

                    {/* Botão de Insights - com animação */}
                    {includeInsights && (
                        <button
                            onClick={() => setIsInsightsModalOpen(true)}
                            className={`relative flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${
                                hasNewInsights
                                    ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700 shadow-lg'
                                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            }`}
                            disabled={!hasNewInsights}
                        >
                            <Sparkles className={`w-4 h-4 ${hasNewInsights ? 'animate-pulse' : ''}`} />
                            Insights
                            {hasNewInsights && (
                                <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs text-white animate-bounce">
                                {insightsCount}
                            </span>
                            )}
                        </button>
                    )}
                </div>

                {/* Summary Cards - Sempre no topo */}
                <SummaryCards
                    data={reportData}
                    isLoading={isLoadingReport}
                    compareWithPrevious={compareWithPrevious}
                />

                {/* Visualização do Relatório */}
                <ReportView
                    data={reportData}
                    isLoading={isLoadingReport}
                    isError={isErrorReport}
                    error={errorReport}
                    selectedMetrics={selectedMetrics}
                    selectedDimensions={selectedDimensions}
                    compareWithPrevious={compareWithPrevious}
                    includeGrowthAnalysis={includeGrowthAnalysis}
                    onRefresh={refetchReport}
                />

                {/* Modal de Filtros Avançados */}
                <ReportFiltersModal
                    isOpen={isFiltersModalOpen}
                    onClose={() => setIsFiltersModalOpen(false)}
                    selectedMetrics={selectedMetrics}
                    onMetricsChange={setSelectedMetrics}
                    selectedDimensions={selectedDimensions}
                    onDimensionsChange={setSelectedDimensions}
                    compareWithPrevious={compareWithPrevious}
                    onCompareChange={setCompareWithPrevious}
                    includeInsights={includeInsights}
                    onInsightsChange={setIncludeInsights}
                    includeGrowthAnalysis={includeGrowthAnalysis}
                    onGrowthAnalysisChange={setIncludeGrowthAnalysis}
                    topN={topN}
                    onTopNChange={setTopN}
                    minImpressions={minImpressions}
                    onMinImpressionsChange={setMinImpressions}
                    alertThreshold={alertThreshold}
                    onAlertThresholdChange={setAlertThreshold}
                    selectedCountry={selectedCountry}
                    onCountryChange={setSelectedCountry}
                    selectedAdUnit={selectedAdUnit}
                    onAdUnitChange={setSelectedAdUnit}
                />

                {/* Modal de Insights */}
                <InsightsModal
                    isOpen={isInsightsModalOpen}
                    onClose={() => setIsInsightsModalOpen(false)}
                    insights={reportData?.insights || []}
                />
            </div>
        );
    }
    if (reportType === 'key_values') {
        return <KeyValueDashboard onBack={handleBackToSelection} />;
    }

}